import { apiClient } from '@/api/client';

export interface SendFriendRecommendationParams {
  recipient_id: string;
  menu_item_id: string;
  check_in_id?: string;
  note?: string;
}

export interface FriendRecommendation {
  id: string;
  sender_id: string;
  recipient_id: string;
  menu_item_id: string;
  check_in_id: string | null;
  note: string | null;
  created_at: string;
  sender_name: string | null;
  sender_username: string | null;
  sender_image: string | null;
  menu_item_name: string | null;
  business_id: string | null;
  business_name: string | null;
  check_in_rating_key: string | null;
}

export interface PaginatedRecommendations {
  items: FriendRecommendation[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Send a recommendation to a friend
 *
 * POST /api/friend-recommendations
 */
export async function sendFriendRecommendation(data: SendFriendRecommendationParams) {
  return apiClient.post<{ message: string; recommendation: FriendRecommendation }>(
    '/api/friend-recommendations',
    data,
  );
}

/**
 * Get recommendations received by the current user (paginated)
 *
 * GET /api/friend-recommendations/received
 */
export async function getReceivedFriendRecommendations(params: { page?: number; per_page?: number } = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.per_page) query.set('per_page', String(params.per_page));
  const qs = query.toString();
  return apiClient.get<PaginatedRecommendations>(`/api/friend-recommendations/received${qs ? `?${qs}` : ''}`);
}

/**
 * Get recommendations sent by the current user (paginated)
 *
 * GET /api/friend-recommendations/sent
 */
export async function getSentFriendRecommendations(params: { page?: number; per_page?: number } = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.per_page) query.set('per_page', String(params.per_page));
  const qs = query.toString();
  return apiClient.get<PaginatedRecommendations>(`/api/friend-recommendations/sent${qs ? `?${qs}` : ''}`);
}

/**
 * Get recommendations for a specific menu item
 *
 * GET /api/friend-recommendations/menu-item/:menuItemId
 */
export async function getMenuItemFriendRecommendations(menuItemId: string) {
  return apiClient.get<{ recommendations: FriendRecommendation[] }>(
    `/api/friend-recommendations/menu-item/${menuItemId}`,
  );
}

/**
 * Get recommendations the current user has sent for a specific menu item
 *
 * GET /api/friend-recommendations/sent/menu-item/:menuItemId
 */
export async function getSentMenuItemRecommendations(menuItemId: string) {
  return apiClient.get<{ recommendations: FriendRecommendation[] }>(
    `/api/friend-recommendations/sent/menu-item/${menuItemId}`,
  );
}
